/**
 * Недельная лента подтверждения времени (задача 116 §2.2, макап 3a).
 *
 * UI-only замена дневной консоли для weekly-юнитов: контекст-бар (листание
 * недели, прогресс «Подтверждено N / M», CTA «Подтвердить всех») + карточки людей,
 * сгруппированные по юнитам. Данные — `week-feed` (§1.2), мутации — `approve-week`
 * / `approve-week-bulk` (§1.3). Дневные эндпоинты для weekly-юнита остаются
 * технически доступны (полномочие то же) — это линза, не дыра (architecture §3.1).
 *
 * Строится строго отдельным компонентом (`components/approval/week-feed/`), а не
 * inline на `TimeApprovalPage` (та уже перегружена): страница лишь ветвит линзу.
 * Групп-заголовки юнитов написаны по образцу реестра 114 §2.1 (общий компонент по
 * правилу трёх пока не выносим). Тона — `--tone-*`, без сырых цветов.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { addDays, format, parseISO } from "date-fns";
import { ru as ruLocale, enUS } from "date-fns/locale";
import { CheckCheck, ChevronLeft, ChevronRight } from "lucide-react";

import { Surface } from "@/components/ui/surface";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PeopleNavProvider } from "@/components/people/PeopleNavContext";
import { EntryDock, type EntryDockSubject } from "@/components/approval/EntryDock";
import { AbsenceFormDialog } from "@/components/people/AbsenceFormDialog";
import {
  useApproveWeek,
  useApproveWeekBulk,
  useUnapproveWeek,
  useWeekFeed,
  WEEK_FEED_KEY,
} from "@/hooks/useApproval";
import type { WeekBulkApproveResult, WeekUnitGroup } from "@/api/approval";
import { WeekPersonCard, type AbsenceTarget } from "./WeekPersonCard";
import { WeekApproveSummaryDialog } from "./WeekApproveSummaryDialog";
import { approvedDates, feedIsEmpty, weekProgress } from "./helpers";

interface WeekFeedConsoleProps {
  weekStart: string;
  /** Выбранный юнит сцены; дочерние включаются только явным флагом. */
  unitId: number | null;
  includeChildren?: boolean;
  enabled?: boolean;
  onWeekStartChange: (weekStart: string) => void;
}

export function WeekFeedConsole({
  weekStart,
  unitId,
  includeChildren = false,
  enabled = true,
  onWeekStartChange,
}: WeekFeedConsoleProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? enUS : ruLocale;
  const qc = useQueryClient();

  const {
    data: feed,
    isLoading,
    isError,
    error,
  } = useWeekFeed(weekStart, unitId, includeChildren, enabled);
  const approveMut = useApproveWeek();
  const unapproveMut = useUnapproveWeek();
  const bulkMut = useApproveWeekBulk();
  const [summary, setSummary] = React.useState<WeekBulkApproveResult | null>(null);

  // Правка дня из ленты (§2.3): клик по ячейке рабочего дня открывает единый
  // день-редактор (`EntryDock` → `TimesheetDayEditor`, канон задачи 79) за этого
  // человека. Свой редактор не строим — тот же док, что в дневной консоли.
  const [dock, setDock] = React.useState<{ subject: EntryDockSubject; date: string } | null>(null);

  const openDay = (subject: EntryDockSubject, date: string): void => setDock({ subject, date });

  // Оформление отсутствия из ленты (§2.3): пустой день закрывается не только
  // внесением часов, но и отсутствием (решение владельца §2). Reuse того же
  // `AbsenceFormDialog` (диапазон, таксономия 78), что дневная консоль — новый
  // диалог не строим. Инвалидация week-feed идёт через `useCreateScheduleEvent`
  // (глобальный `invalidateApprovalQueries`, ключ `approval-week-feed`).
  const [absence, setAbsence] = React.useState<AbsenceTarget | null>(null);

  // По закрытию дока (в т.ч. после финала «Подтвердить» = табель + approve, или
  // добавления/правки записей) перечитываем ленту: ячейка дня и счётчики недели
  // обновляются, пустой день перестаёт блокировать «Подтвердить».
  const closeDock = (): void => {
    setDock(null);
    void qc.invalidateQueries({ queryKey: WEEK_FEED_KEY(weekStart, unitId, includeChildren) });
  };

  const shiftWeek = (delta: number): void => {
    onWeekStartChange(format(addDays(parseISO(weekStart), delta), "yyyy-MM-dd"));
  };

  const from = parseISO(weekStart);
  const to = addDays(from, 6);
  const rangeLabel = `${format(from, "dd.MM", { locale })} – ${format(to, "dd.MM", {
    locale,
  })} ${format(to, "yyyy", { locale })}`;

  const progress = feed ? weekProgress(feed) : { confirmed: 0, total: 0, notRequired: 0 };
  const progressPct = progress.total > 0 ? (progress.confirmed / progress.total) * 100 : 0;

  const rowPending = (userId: number): boolean =>
    (approveMut.isPending && approveMut.variables?.userId === userId) ||
    (unapproveMut.isPending && unapproveMut.variables?.userId === userId);

  const handleBulk = (): void => {
    if (unitId !== null) {
      bulkMut.mutate(
        { weekStart, unitId, includeChildren },
        { onSuccess: (result) => setSummary(result) },
      );
    }
  };

  return (
    <PeopleNavProvider enabled>
      <div className="flex flex-col gap-4">
        {/* Контекст-бар: листание недели + прогресс + «Подтвердить всех». */}
        <Surface className="flex flex-wrap items-center gap-3 p-3">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t("timeApproval.prevWeek")}
              data-testid="week-prev"
              onClick={() => shiftWeek(-7)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold text-[var(--fg-1)] tabular-nums">
              {rangeLabel}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t("timeApproval.nextWeek")}
              data-testid="week-next"
              onClick={() => shiftWeek(7)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-1 items-center justify-end gap-3">
            {/* Прогресс показываем только когда есть кого считать — пустая пилюля
                «0 / 0» на загрузке/пустой ленте не нужна (прецедент задачи 22 §4.10). */}
            {progress.total > 0 && (
              <>
                <span
                  className="font-condensed text-xs uppercase tracking-[0.06em] text-[var(--fg-3)]"
                  data-testid="week-progress-label"
                >
                  {t("week.progress", { confirmed: progress.confirmed, total: progress.total })}
                </span>
                <Progress value={progressPct} tone="success" className="h-1.5 w-32" />
              </>
            )}
            {progress.notRequired > 0 && (
              <Badge tone="neutral" emphasis="soft" data-testid="week-not-required-count">
                {t("week.notRequiredCount", { count: progress.notRequired })}
              </Badge>
            )}
            {progress.total === 0 ? (
              <span className="text-sm text-[var(--fg-3)]" data-testid="week-nothing-to-approve">
                {t("week.nothingToApprove")}
              </span>
            ) : (
              <Button
                size="default"
                data-testid="week-approve-all"
                disabled={bulkMut.isPending || progress.confirmed === progress.total}
                onClick={handleBulk}
              >
                <CheckCheck className="h-4 w-4" />
                {t("week.approveAll")}
              </Button>
            )}
          </div>
        </Surface>

        {isError ? (
          <Surface className="p-4 text-sm text-[var(--tone-danger-fg)]" data-testid="week-error">
            <ApiErrorNotice error={error} fallbackMessage={t("common.apiError.fallback")} />
          </Surface>
        ) : isLoading || !feed ? (
          <Surface className="p-4 text-sm text-[var(--fg-3)]" data-testid="week-loading">
            {t("common.loading")}
          </Surface>
        ) : feedIsEmpty(feed) ? (
          <Surface className="p-4 text-sm text-[var(--fg-3)]" data-testid="week-empty">
            {t("week.empty")}
          </Surface>
        ) : (
          feed.groups
            .filter((g) => g.rows.length > 0)
            .map((group) => (
              <UnitGroup
                key={group.unit_id}
                group={group}
                rowPending={rowPending}
                onApprove={(userId) =>
                  approveMut.mutate({ userId, weekStart, unitId, includeChildren })
                }
                onUnapprove={(userId, dates) =>
                  unapproveMut.mutate({
                    userId,
                    weekStart,
                    unitId,
                    includeChildren,
                    approvedDates: dates,
                  })
                }
                selectedDay={
                  dock === null ? null : { userId: dock.subject.userId, date: dock.date }
                }
                onOpenDay={openDay}
                onOpenAbsence={setAbsence}
              />
            ))
        )}
      </div>

      {/* Единый день-редактор за сотрудника (§2.3): правый док, канон задачи 79. */}
      <EntryDock
        subject={dock?.subject ?? null}
        date={dock?.date ?? weekStart}
        weekStart={weekStart}
        unitId={unitId}
        surface="desktop-dock"
        modal={false}
        onInteractOutside={(event) => {
          const target = event.target;
          if (target instanceof Element && target.closest('[data-week-day-selectable="true"]')) {
            event.preventDefault();
          }
        }}
        onClose={closeDock}
      />

      {/* Отсутствие на ДИАПАЗОН дат (§2.3): тот же `AbsenceFormDialog`, что дневная
          консоль и карточка `/people/$id` (таксономия 78, задача 82 §1.2).
          Преднабиваем первым пустым днём; scope «своё поддерево» держит backend
          `/people/{id}/schedule`. Успех инвалидирует week-feed (глобальный
          `invalidateApprovalQueries`) → день становится «отсутствие», перестаёт
          блокировать «Подтвердить». */}
      {absence !== null && (
        <AbsenceFormDialog
          open
          userId={absence.userId}
          defaultDate={absence.defaultDate}
          onOpenChange={(v) => !v && setAbsence(null)}
        />
      )}

      <WeekApproveSummaryDialog result={summary} onClose={() => setSummary(null)} />
    </PeopleNavProvider>
  );
}

function UnitGroup({
  group,
  rowPending,
  onApprove,
  onUnapprove,
  selectedDay,
  onOpenDay,
  onOpenAbsence,
}: {
  group: WeekUnitGroup;
  rowPending: (userId: number) => boolean;
  onApprove: (userId: number) => void;
  onUnapprove: (userId: number, approvedDates: string[]) => void;
  selectedDay: { userId: number; date: string } | null;
  onOpenDay: (subject: EntryDockSubject, date: string) => void;
  onOpenAbsence: (target: AbsenceTarget) => void;
}): React.ReactElement {
  return (
    <section className="flex flex-col gap-3" data-testid={`week-group-${group.unit_id}`}>
      {/* Групп-заголовок юнита — образец реестра 114 §2.1 (caps-condensed на одной
          линии-филлере; `border-b` контейнера не добавляем — двойная линейка). */}
      <div className="flex items-center gap-2">
        <span className="font-condensed text-xs font-semibold uppercase tracking-[0.06em] text-[var(--fg-2)]">
          {group.unit_name}
        </span>
        <span className="h-px flex-1 bg-[var(--rule-line)]" />
      </div>
      {group.rows.map((row) => (
        <WeekPersonCard
          key={row.user.id}
          row={row}
          pending={rowPending(row.user.id)}
          onApprove={() => onApprove(row.user.id)}
          onUnapprove={() => onUnapprove(row.user.id, approvedDates(row))}
          selectedDate={selectedDay?.userId === row.user.id ? selectedDay.date : null}
          onOpenDay={onOpenDay}
          onOpenAbsence={onOpenAbsence}
        />
      ))}
    </section>
  );
}
